import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import type { BlogPost } from '@/types/blog';
import { siteUrl } from '@/lib/seo/site-url';

interface CopyPostButtonProps {
  post: BlogPost;
  className?: string;
}

/**
 * Formats a blog post for LLM context consumption
 * Includes: title, metadata, full content
 */
function formatPostForLLM(post: BlogPost): string {
  const author = post.schema?.author || 'Johnny Vaz';
  const tags = post.tags || [];
  
  const metadata = [
    `# ${post.title}`,
    '',
    `**Autor:** ${author}`,
    `**Data:** ${post.date.toLocaleDateString('pt-BR')}`,
    `**Categorias:** ${post.categories.join(', ')}`,
    ...(tags.length > 0 ? [`**Tags:** ${tags.join(', ')}`] : []),
    `**Tempo de leitura:** ${post.readingTime} min`,
    '',
    '---',
    '',
  ];

  // Include excerpt if available
  if (post.excerpt) {
    metadata.push(`**Resumo:** ${post.excerpt}`, '', '---', '');
  }

  // Combine metadata with content
  const formatted = [
    ...metadata,
    post.content, // Raw markdown content
    '',
    '---',
    '',
    `**URL:** ${siteUrl(post.url)}`,
    '',
    `_Post copiado do blog de Johnny Vaz - Expert em IA e Automação_`,
  ].join('\n');

  return formatted;
}

/**
 * Button component to copy blog post formatted for LLM context
 * Follows UI/UX AI Pattern 4: Direct LLM Integration
 */
export function CopyPostButton({ post, className = '' }: CopyPostButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      console.log('📋 Copiando post para clipboard...', post.title);
      const formattedPost = formatPostForLLM(post);
      console.log('📄 Post formatado:', formattedPost.substring(0, 200) + '...');
      
      await navigator.clipboard.writeText(formattedPost);
      console.log('✅ Post copiado com sucesso!');
      
      setCopied(true);
      
      // Reset icon after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('❌ Erro ao copiar post:', error);
      alert('Erro ao copiar. Verifique as permissões do navegador.');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors ${className}`}
      title="Copiar post formatado para IA (ChatGPT, Claude, etc.)"
      aria-label="Copiar post formatado para IA"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
          <span className="hidden sm:inline">Copiado!</span>
        </>
      ) : (
        <>
          <Copy className="w-4 h-4" />
          <span className="hidden sm:inline">Copiar para IA</span>
        </>
      )}
    </button>
  );
}
